import { useEffect, useMemo, useState, useCallback } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Activity, Bell, Download, LayoutGrid, Save, LogOut, FileText,
  FileSpreadsheet, Check, RotateCcw, Trash2, Upload, X, Sparkles,
} from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveSummary } from "@/lib/useLive";
import { SortableWidget } from "@/components/dashboard/SortableWidget";
import { AlertsDialog, AddWidgetDialog, ShareDialog, UsersDialog, SettingsDialog, NewDashboardDialog, DataSourceDialog, RenameDashboardDialog, DashShareDialog, SendReportDialog, TeamDialog } from "@/components/dashboard/Panels";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const RANGE_LABELS = { "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", "180d": "Last 180 days", "360d": "Last 360 days", "720d": "Last 720 days", "1080d": "Last 1080 days" };

function NotificationsPopover({ tick }) {
  const [notes, setNotes] = useState([]);
  const load = useCallback(() => api.get("/notifications").then((r) => setNotes(r.data)), []);
  useEffect(() => { load(); }, [load, tick]);
  const unread = notes.filter((n) => !n.read).length;

  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 flex items-center justify-center rounded-md border border-border hover:bg-secondary transition-colors" data-testid="notifications-button">
          <Bell className="h-4 w-4" />
          {unread > 0 && <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-[10px] font-bold text-white flex items-center justify-center" data-testid="notifications-badge">{unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">Notifications</span>
          {unread > 0 && <button onClick={readAll} className="text-xs text-primary hover:underline" data-testid="mark-all-read-button">Mark all read</button>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notes.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">All clear — no alerts triggered</p>}
          {notes.map((n) => (
            <div key={n.id} className={`p-3 border-b border-border last:border-0 flex gap-2.5 ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.severity === "danger" ? "bg-destructive" : "bg-[hsl(45_93%_47%)]"}`} />
              <div>
                <div className="text-sm leading-snug">{n.message}</div>
                <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Dashboard() {
  const { user, logout, subscription } = useAuth();
  const navigate = useNavigate();
  const canEdit = user.role === "admin" || user.role === "editor";
  const isAdmin = user.role === "admin";

  const [dashboards, setDashboards] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [datasetId, setDatasetId] = useState("builtin");
  const [dashName, setDashName] = useState("");
  const [datasets, setDatasets] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [options, setOptions] = useState({ regions: [], teams: [], ranges: [] });
  const [filters, setFilters] = useState({ region: "all", team: "all", range: "30d" });
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [readonly, setReadonly] = useState(false);
  const [dataOpen, setDataOpen] = useState(false);
  const [dataMode, setDataMode] = useState("list");
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const openImport = () => { setDataMode("csv"); setDataOpen(true); };
  const handleDataOpenChange = (o) => { setDataOpen(o); if (!o) setDataMode("list"); };
  const hasCustomData = datasets.some((d) => d.id !== "builtin");

  const isBuiltin = datasetId === "builtin";
  const { summary, tick, live } = useLiveSummary(filters, datasetId);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadDatasets = () => api.get("/datasets").then((r) => setDatasets(r.data));

  useEffect(() => {
    api.get("/filters/options").then((r) => setOptions(r.data));
    loadDatasets();
    api.get("/dashboards").then((r) => {
      setDashboards(r.data);
      if (r.data.length) setCurrentId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!currentId) return;
    api.get(`/dashboards/${currentId}`).then((r) => {
      setWidgets(r.data.widgets || []);
      setDatasetId(r.data.dataset_id || "builtin");
      setDashName(r.data.name);
      setReadonly(!!r.data.readonly);
      if (r.data.readonly) setEditMode(false);
      setDirty(false);
      const catUrl = (r.data.dataset_id || "builtin") === "builtin"
        ? "/metrics/catalog" : `/datasets/${r.data.dataset_id}/catalog`;
      api.get(catUrl).then((c) => {
        const map = {};
        c.data.forEach((m) => { map[m.key] = m; });
        setCatalog(map);
      }).catch(() => setCatalog({}));
    });
  }, [currentId]);

  const summaryMap = useMemo(() => {
    const m = {};
    summary.forEach((s) => { m[s.key] = s; });
    return m;
  }, [summary]);

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      setWidgets((items) => {
        const oldI = items.findIndex((w) => w.id === active.id);
        const newI = items.findIndex((w) => w.id === over.id);
        return arrayMove(items, oldI, newI);
      });
      setDirty(true);
    }
  };

  const removeWidget = (id) => { setWidgets((w) => w.filter((x) => x.id !== id)); setDirty(true); };
  const addWidget = (w) => { setWidgets((prev) => [...prev, w]); setDirty(true); };
  const configureWidget = (id, wf) => {
    setWidgets((ws) => ws.map((w) => {
      if (w.id !== id) return w;
      const nw = { ...w };
      if (wf) nw.filters = wf; else delete nw.filters;
      return nw;
    }));
    setDirty(true);
  };

  const saveLayout = async () => {
    try {
      await api.put(`/dashboards/${currentId}`, { widgets });
      setDirty(false);
      toast.success("Layout saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const resetLayout = () => {
    api.get(`/dashboards/${currentId}`).then((r) => { setWidgets(r.data.widgets || []); setDirty(false); });
  };

  const createDashboard = async (name, dsId) => {
    const { data } = await api.post("/dashboards", { name, dataset_id: dsId });
    const list = await api.get("/dashboards");
    setDashboards(list.data);
    setCurrentId(data.id);
    setEditMode(false);
    toast.success("Dashboard created");
  };

  const deleteDashboard = async () => {
    try {
      await api.delete(`/dashboards/${currentId}`);
      const list = await api.get("/dashboards");
      setDashboards(list.data);
      setCurrentId(list.data[0]?.id || null);
      toast.success("Dashboard deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const doExport = (type) => {
    const q = new URLSearchParams(filters).toString();
    window.open(`${API}/export/${type}?${q}`, "_blank");
    toast.success(`${type.toUpperCase()} report generated`);
  };

  const exportTimeseries = (format = "csv") => {
    const q = new URLSearchParams({ dataset_id: datasetId, range: filters.range, region: filters.region, team: filters.team, format, name: dashName || "dashboard" }).toString();
    window.open(`${API}/export/timeseries?${q}`, "_blank");
    toast.success(`${format.toUpperCase()} time-series exported`);
  };

  const currentDash = dashboards.find((d) => d.id === currentId);
  const sharedToTeam = !!currentDash?.shared_to_team;
  const reloadDashboards = () => api.get("/dashboards").then((r) => setDashboards(r.data));

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <Activity className="h-4.5 w-4.5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold tracking-tight hidden sm:block">PULSE / KPI</span>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider ml-1" data-testid="user-role-badge">{user.role}</Badge>
            {subscription && !subscription.is_admin && (
              <button
                onClick={() => navigate("/pricing")}
                className="hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                data-testid="manage-plan-button"
                title="Manage subscription"
              >
                {subscription.plan || "plan"} · {subscription.status}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <NotificationsPopover tick={tick} />
            <div className="hidden md:flex items-center gap-2">
              <AlertsDialog catalog={catalog} canEdit={canEdit} />
              {currentId && !readonly && <SendReportDialog dashId={currentId} range={filters.range} />}
              {!readonly && <TeamDialog dashId={currentId} shared={sharedToTeam} onShareChange={reloadDashboards} />}
              {canEdit && <ShareDialog />}
              {isAdmin && <SettingsDialog />}
              {isAdmin && <UsersDialog />}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-timeseries-button"><Download className="h-4 w-4 mr-1.5" />Export Data</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportTimeseries("csv")} data-testid="export-timeseries-csv-button"><FileSpreadsheet className="h-4 w-4 mr-2" />Export CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportTimeseries("xlsx")} data-testid="export-timeseries-xlsx-button"><FileSpreadsheet className="h-4 w-4 mr-2" />Export Excel (.xlsx)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {isBuiltin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="export-button"><Download className="h-4 w-4 mr-1.5" />Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => doExport("csv")} data-testid="export-csv-button"><FileSpreadsheet className="h-4 w-4 mr-2" />Export CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="export-pdf-button"><FileText className="h-4 w-4 mr-2" />Export PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              )}
            </div>
            <button onClick={logout} className="h-9 w-9 flex items-center justify-center rounded-md border border-border hover:bg-secondary hover:text-destructive transition-colors" data-testid="logout-button" title="Sign out">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="px-4 sm:px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Command Center</p>
            <div className="flex items-center gap-2.5">
              <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="dashboard-title">{dashName || "Dashboard"}</h1>
              <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${live ? "border-[hsl(142_71%_45%)]/40 text-[hsl(142_71%_55%)]" : "border-border text-muted-foreground"}`} data-testid="live-indicator">
                <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-[hsl(142_71%_45%)] animate-pulse" : "bg-muted-foreground"}`} />
                {live ? "Live · WebSocket" : "Polling"}
              </span>
              {!isBuiltin && <Badge variant="secondary" className="text-[10px] uppercase" data-testid="custom-data-badge">custom data</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={currentId || ""} onValueChange={(v) => { setCurrentId(v); setEditMode(false); }}>
              <SelectTrigger className="w-[190px] h-9" data-testid="dashboard-switcher"><SelectValue placeholder="Select dashboard" /></SelectTrigger>
              <SelectContent>{dashboards.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <NewDashboardDialog datasets={datasets} onCreate={createDashboard} />
            <DataSourceDialog datasets={datasets} onChange={loadDatasets} onCreateDashboard={createDashboard} open={dataOpen} onOpenChange={handleDataOpenChange} startMode={dataMode} />
            {currentId && !readonly && <RenameDashboardDialog dashId={currentId} currentName={dashName} onRenamed={() => api.get("/dashboards").then((r) => { setDashboards(r.data); const c = r.data.find((d) => d.id === currentId); if (c) setDashName(c.name); })} />}
            {currentId && !readonly && <DashShareDialog dashId={currentId} />}
            {dashboards.length > 1 && !readonly && (
              <Button variant="outline" size="sm" onClick={deleteDashboard} data-testid="delete-dashboard-button" title="Delete this dashboard">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-3">
          <Select value={filters.range} onValueChange={(v) => setFilters({ ...filters, range: v })}>
            <SelectTrigger className="w-[150px] h-9" data-testid="filter-range-select"><SelectValue /></SelectTrigger>
            <SelectContent>{options.ranges.map((r) => <SelectItem key={r} value={r}>{RANGE_LABELS[r] || r}</SelectItem>)}</SelectContent>
          </Select>
          {isBuiltin && (
            <>
              <Select value={filters.region} onValueChange={(v) => setFilters({ ...filters, region: v })}>
                <SelectTrigger className="w-[150px] h-9" data-testid="filter-region-select"><SelectValue placeholder="Region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {options.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.team} onValueChange={(v) => setFilters({ ...filters, team: v })}>
                <SelectTrigger className="w-[140px] h-9" data-testid="filter-team-select"><SelectValue placeholder="Team" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teams</SelectItem>
                  {options.teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          {readonly ? (
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wider h-9 flex items-center px-3" data-testid="readonly-badge">Shared with you · read-only</Badge>
          ) : (
            <Button variant={editMode ? "default" : "outline"} size="sm" onClick={() => setEditMode(!editMode)} data-testid="toggle-edit-button">
              <LayoutGrid className="h-4 w-4 mr-1.5" />{editMode ? "Editing" : "Customize"}
            </Button>
          )}
        </div>

        {editMode && (
          <div className="mt-3 flex items-center gap-2 flex-wrap p-3 rounded-md border border-primary/30 bg-primary/5" data-testid="edit-toolbar">
            <span className="text-xs text-muted-foreground mr-1">Drag widgets to reorder · remove with ✕ · add new below</span>
            <AddWidgetDialog catalog={catalog} onAdd={addWidget} allowBar={isBuiltin} />
            <Button size="sm" onClick={saveLayout} disabled={!dirty} data-testid="save-layout-button"><Save className="h-4 w-4 mr-1.5" />Save</Button>
            <Button size="sm" variant="outline" onClick={resetLayout} data-testid="reset-layout-button"><RotateCcw className="h-4 w-4 mr-1.5" />Reset</Button>
            {dirty && <span className="text-xs text-[hsl(45_93%_55%)]">Unsaved changes</span>}
          </div>
        )}
      </div>

      {/* Onboarding: prompt to import real data */}
      {!hasCustomData && !bannerDismissed && (
        <div className="mx-4 sm:mx-6 mt-4 flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="import-onboarding-banner">
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 text-primary"><Sparkles className="h-4 w-4" /></div>
            <div>
              <p className="text-sm font-medium">You're viewing sample data</p>
              <p className="text-xs text-muted-foreground">Import your own CSV or Excel to build a live dashboard in seconds.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={openImport} data-testid="banner-import-button"><Upload className="h-4 w-4 mr-1.5" />Import your data</Button>
            <button onClick={() => setBannerDismissed(true)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="banner-dismiss-button" title="Dismiss"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {/* Grid */}
      <main className="p-4 sm:p-6">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[minmax(140px,auto)]" data-testid="dashboard-grid">
              {widgets.map((w) => (
                <SortableWidget
                  key={w.id}
                  widget={w}
                  editMode={editMode}
                  onRemove={removeWidget}
                  onConfigure={configureWidget}
                  options={options}
                  summaryMap={summaryMap}
                  filters={filters}
                  tick={tick}
                  catalog={catalog}
                  dataBase={isBuiltin ? "/metrics" : `/datasets/${datasetId}`}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {widgets.length === 0 && (
          <div className="border border-dashed border-border rounded-md py-20 text-center text-muted-foreground" data-testid="empty-grid">
            <p className="font-mono text-sm">No widgets. Enter Customize mode to add some.</p>
          </div>
        )}
      </main>
    </div>
  );
}
